<script lang="ts" setup>
import CardContainer from '@/components/common/CardContainer.vue'
import IfViewport from '@/components/common/IfViewport.vue'
import NotificationBanner from '@/components/common/NotificationBanner.vue'
import { useLeaguesStore } from '@/store/public/leagues'
import {
  formatDate,
  formatFloat,
  formatTime,
  formatValue,
} from '@/utils/format'

const leagues = useLeaguesStore()
</script>

<template>
  <CardContainer
    v-for="(section, i) in leagues.selectedLeague?.sections ?? []"
    :key="`section-${i}`"
    :title="$t('leagues.labels.section', { section: section.number })"
  >
    <div class="league-section-teams">
      <h4>{{ $t('leagues.labels.teams') }}</h4>

      <NotificationBanner
        v-if="section.teams.length === 0"
        :message="$t('leagues.errors.no-teams')"
        is-warning
      />

      <table v-else class="table">
        <thead>
          <tr>
            <th>{{ $t('leagues.labels.name') }}</th>
            <th>{{ $t('leagues.labels.city') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(team, j) in section.teams" :key="`team-${i}-${j}`">
            <td>{{ formatValue(team.name) }}</td>
            <td>{{ formatValue(team.city) }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="league-section-schedule">
      <h4>{{ $t('leagues.labels.schedule') }}</h4>

      <NotificationBanner
        v-if="section.rounds.length === 0"
        :message="$t('leagues.errors.no-rounds')"
        is-warning
      />

      <table v-else class="table">
        <thead>
          <tr>
            <th class="align-right fit-content">Round</th>
            <th>{{ $t('leagues.labels.date-time') }}</th>
            <th>{{ $t('leagues.labels.bowling-alley') }}</th>

            <IfViewport min-width="768">
              <th>{{ $t('leagues.labels.city') }}</th>
            </IfViewport>

            <th class="fit-content"></th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="(round, j) in section.rounds" :key="`round-${i}-${j}`">
            <td class="align-right fit-content">
              {{ formatFloat(round.number, 0) }}
            </td>
            <td>
              <IfViewport min-width="768">
                {{ formatDate(new Date(round.start_time), true, true) }}
              </IfViewport>

              <IfViewport max-width="767">
                {{ formatDate(new Date(round.start_time), false, true) }}
                <br />
                {{ formatTime(new Date(round.start_time)) }}
              </IfViewport>
            </td>
            <td>
              {{ formatValue(round.bowling_alley_name) }}

              <IfViewport max-width="767">
                <br />{{ formatValue(round.city) }}
              </IfViewport>
            </td>

            <IfViewport min-width="768">
              <td>{{ formatValue(round.city) }}</td>
            </IfViewport>

            <td class="fit-content noprint noselect">
              <RouterLink
                :to="{
                  path: `/leagues/${leagues.selectedSeason}/${leagues.selectedLeague?.id}/standings/${round.id}`,
                }"
              >
                <small>
                  <FontAwesomeIcon class="icon" icon="file-contract" />

                  <IfViewport min-width="768">
                    <span>{{ $t('leagues.labels.standings') }}</span>
                  </IfViewport>
                </small>
              </RouterLink>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </CardContainer>
</template>

<style lang="scss" scoped>
.icon {
  margin-right: 0.25em;
}
</style>
